/**
 * @file        src/pages/platform/PlatformAdminUserList.jsx
 * @description Admin-console login accounts (super_admin only) — every
 *              super_admin and every tenant's tenant_admin, in one table.
 *              Tenant filter dropdown reuses the tenants list.
 * @author      Kinetika Labs
 * @created     2026-07-27
 * @changelog
 *   - 2026-07-27 Claude — Initial (multi-tenant super admin platform).
 *   - 2026-09-25 Claude — `keepPreviousData: true` is a React Query **v4** option
 *                          and this workspace is on v5 (5.101), where no such
 *                          option exists and it was silently ignored. So every
 *                          page / filter / search change dropped `data` to
 *                          undefined, the table was swapped for six skeleton rows
 *                          and the panel collapsed ~700px before growing back —
 *                          the "pages flicker / the page moves" report. v5 spells
 *                          it `placeholderData: keepPreviousData` (a FUNCTION
 *                          imported from the package, not a boolean).
 */
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import PageHead from '../../components/PageHead';
import Icon from '../../components/Icon';
import Chip from '../../components/Chip';
import Pager from '../../components/Pager';
import TableSkeleton from '../../components/TableSkeleton';
import Select from '../../components/Select';
import { get } from '../../api/client';
import { qk } from '../../api/queryKeys';

export default function PlatformAdminUserList() {
  const [page, setPage] = useState(1);
  const [pageSize] = useState(25);
  const [search, setSearch] = useState('');
  const [applied, setApplied] = useState('');
  const [tenantId, setTenantId] = useState('');

  const { data: tenantsData } = useQuery({
    queryKey: qk.tenants.list({ pageSize: 200 }),
    queryFn: () => get('/tenants', { params: { pageSize: 200 } }),
  });
  const tenants = tenantsData?.data || [];
  const tenantById = useMemo(() => Object.fromEntries(tenants.map((t) => [String(t.id), t.name])), [tenants]);
  const tenantOptions = [{ value: '', label: 'All tenants' }, ...tenants.map((t) => ({ value: String(t.id), label: t.name }))];

  const params = { page, pageSize };
  if (applied) params.q = applied;
  if (tenantId) params.tenantId = tenantId;

  const { data, isLoading } = useQuery({
    queryKey: qk.platformAdminUsers.list(params),
    queryFn: () => get('/platform-admin-users', { params }),
    placeholderData: keepPreviousData,
  });

  const rows = data?.data || [];
  const total = data?.meta?.total || 0;

  return (
    <>
      <PageHead
        title="Platform Admin Users"
        subtitle="Platform · admin-console login accounts"
        actions={<Link to="/platform/admin-users/new" className="btn primary"><Icon name="plus" /> Add Admin User</Link>}
      />

      <article className="panel glass">
        <form className="search-row" onSubmit={(e) => { e.preventDefault(); setPage(1); setApplied(search); }}>
          <div className="field">
            <label>Name / Email</label>
            <input placeholder="Search admin users…" value={search} onChange={(e) => setSearch(e.target.value)} />
          </div>
          <div className="field">
            <label>Tenant</label>
            <Select value={tenantId} onChange={(v) => { setPage(1); setTenantId(v); }} options={tenantOptions} />
          </div>
          <button className="btn primary" type="submit"><Icon name="search" /> Search</button>
        </form>

        <div className="table-wrap">
          {isLoading ? <TableSkeleton /> : (
            <table className="table">
              <thead>
                <tr><th>#</th><th>Name</th><th>Email</th><th>Role</th><th>Tenant</th><th>Status</th><th>Action</th></tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr><td colSpan={7} className="empty">No admin users yet. Click “Add Admin User” to create one.</td></tr>
                ) : rows.map((u, idx) => (
                  <tr key={u.id}>
                    <td className="mono">{(page - 1) * pageSize + idx + 1}</td>
                    <td><b>{`${u.first_name || ''} ${u.last_name || ''}`.trim() || '—'}</b></td>
                    <td>{u.email}</td>
                    <td><Chip tone={u.role === 'super_admin' ? 'warn' : 'ok'}>{u.role === 'super_admin' ? 'Super Admin' : 'Tenant Admin'}</Chip></td>
                    <td>{u.tenant_id ? (tenantById[String(u.tenant_id)] || `#${u.tenant_id}`) : <span className="muted">—</span>}</td>
                    <td><Chip tone={u.is_active ? 'ok' : 'rej'}>{u.is_active ? 'Active' : 'Inactive'}</Chip></td>
                    <td className="row-act">
                      <Link to={`/platform/admin-users/${u.id}/edit`} className="ib edit" aria-label="Edit"><Icon name="edit" /></Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <Pager page={page} pageSize={pageSize} total={total} onPage={setPage} />
      </article>
    </>
  );
}
